import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  IconButton,
  Divider,
  Avatar,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Support as SupportIcon,
  Home as HomeIcon,
  Mail as MailIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { API_URL } from '../../utils/config';
import { format, formatDistanceToNow } from 'date-fns';
import { useMessagePolling } from '../../hooks/useMessagePolling';
import { brandColors } from '../../theme';

interface User {
  id: string;
  name: string;
  email: string;
}

interface Message {
  id: number;
  content: string;
  sender: {
    id: string;
    name: string;
    type: 'user' | 'admin';
  };
  createdAt: string;
  isRead: boolean;
}

interface Thread {
  id: number;
  subject: string;
  category: string | null;
  status: 'open' | 'pending' | 'resolved' | 'closed';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  createdBy: User;
  createdAt: string;
}

interface Participant {
  id: string;
  name: string;
  type: 'customer' | 'admin' | 'observer';
}

interface ThreadResponse {
  success: boolean;
  data?: {
    thread: Thread;
    messages: Message[];
    participants: Participant[];
  };
}

const MessageThreadPage: React.FC = () => {
  const { threadId } = useParams<{ threadId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [replyContent, setReplyContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const messagesEndRef = useRef<any>(null);
  const messageContainerRef = useRef<any>(null);

  // Enable polling for this specific thread
  useMessagePolling({
    enabled: true,
    interval: 15000, // 15 seconds for active thread
    onMessagesPage: false,
    currentThreadId: threadId ? parseInt(threadId) : undefined,
  });

  // Fetch thread details and messages
  const { data: threadData, isLoading, error } = useQuery({
    queryKey: ['messages', 'thread', threadId],
    queryFn: async () => {
      if (!threadId) throw new Error('Thread ID is required');

      const response = await authenticatedFetch(
        `${API_URL}/api/store/messages/threads/${threadId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response?.ok) {
        if (response?.status === 404) {
          throw new Error('Thread not found');
        }
        throw new Error('Failed to fetch thread');
      }

      const data: ThreadResponse = await response.json();
      return data;
    },
    staleTime: 5000, // Consider data stale after 5 seconds
  });

  // Mark thread as read
  const markAsReadMutation = useMutation({
    mutationFn: async () => {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/messages/threads/${threadId}/read`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response?.ok) {
        throw new Error('Failed to mark as read');
      }

      return response.json();
    },
    onSuccess: () => {
      // Invalidate unread count
      queryClient.invalidateQueries({ queryKey: ['messages', 'unread-count'] });
      // Invalidate thread list to update unread badges
      queryClient.invalidateQueries({ queryKey: ['messages', 'threads'] });
    },
  });

  // Send reply mutation
  const sendReplyMutation = useMutation({
    mutationFn: async (content: string) => {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/messages/threads/${threadId}/messages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ content }),
        }
      );

      if (!response?.ok) {
        throw new Error('Failed to send reply');
      }

      return response.json();
    },
    onSuccess: () => {
      setReplyContent('');
      // Update thread list to reflect the new message
      queryClient.invalidateQueries({ queryKey: ['messages', 'threads'] });
    },
  });

  // Mark as read when thread is loaded
  useEffect(() => {
    if (threadData?.data && threadId) {
      const hasUnreadMessages = threadData.data.messages.some(msg => !msg.isRead);
      if (hasUnreadMessages) {
        markAsReadMutation.mutate();
      }
    }
  }, [threadData, threadId]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [threadData?.data?.messages]);

  const handleSendReply = async () => {
    if (!replyContent.trim()) return;

    setIsSubmitting(true);
    try {
      await sendReplyMutation.mutateAsync(replyContent);
      // Navigate back to messages page after successful send
      navigate('/messages');
    } catch (error) {
      console.error('Failed to send reply:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleSendReply();
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'info';
      case 'pending':
        return 'warning';
      case 'resolved':
        return 'success';
      case 'closed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent':
        return 'error';
      case 'high':
        return 'warning';
      case 'normal':
        return 'primary';
      case 'low':
        return 'default';
      default:
        return 'default';
    }
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          {error instanceof Error && error.message === 'Thread not found' 
            ? 'This conversation could not be found.'
            : 'Failed to load conversation. Please try again later.'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/messages')}
          sx={{ mt: 2 }}
        >
          Back to Messages
        </Button>
      </Container>
    );
  }

  const thread = threadData?.data?.thread;
  const messages = threadData?.data?.messages || [];

  const isThreadClosed = thread?.status === 'closed' || thread?.status === 'resolved';

  const handleArchiveThread = async () => {
    if (!threadId) return;
    
    try {
      const response = await authenticatedFetch(
        `${API_URL}/api/store/messages/archive`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            threadIds: [parseInt(threadId)],
            archive: true
          })
        }
      );

      if (!response?.ok) {
        console.error('Failed to archive thread');
        return;
      }

      // Navigate back to messages page after successful archive
      navigate('/messages');
    } catch (error) {
      console.error('Error archiving thread:', error);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Home
        </Link>
        <Link
          color="inherit"
          href="/messages"
          onClick={(e) => {
            e.preventDefault();
            navigate('/messages');
          }}
          sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
        >
          <MailIcon sx={{ mr: 0.5 }} fontSize="inherit" />
          Messages
        </Link>
        <Typography color="text.primary">
          {thread?.subject}
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
          <IconButton onClick={() => navigate('/messages')} edge="start">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="h1">
              {thread?.subject}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
              <Chip
                label={thread?.status}
                size="small"
                color={getStatusColor(thread?.status || 'open') as any}
              />
              {thread?.priority && thread.priority !== 'normal' && (
                <Chip
                  label={thread.priority}
                  size="small"
                  color={getPriorityColor(thread.priority) as any}
                  variant="outlined"
                />
              )}
              {thread?.category && (
                <Chip
                  label={thread.category}
                  size="small"
                  variant="outlined"
                />
              )}
              <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                Created {thread && formatDistanceToNow(new Date(thread.createdAt), { addSuffix: true })}
              </Typography>
            </Stack>
          </Box>
          {/* Archive button for resolved/closed threads */}
          {isThreadClosed && (
            <Button
              variant="outlined"
              startIcon={<ArchiveIcon />}
              onClick={handleArchiveThread}
              sx={{
                borderColor: brandColors.mediumGray,
                color: brandColors.mediumGray,
                '&:hover': {
                  borderColor: brandColors.darkGray,
                  backgroundColor: 'action.hover',
                },
              }}
            >
              Archive
            </Button>
          )}
        </Stack>
      </Paper>

      {/* Messages */}
      <Paper sx={{ p: 3, mb: 3, maxHeight: '60vh', overflow: 'auto' }} ref={messageContainerRef}>
        {messages.length === 0 ? (
          <Typography color="text.secondary" textAlign="center">
            No messages yet
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {messages.map((message, index) => {
              const isAdmin = message.sender.type === 'admin';
              const isUser = !isAdmin;
              const currentMessageDate = new Date(message.createdAt);
              const previousMessageDate = index > 0 ? new Date(messages[index - 1].createdAt) : null;
              
              // Show date breaker when date changes or for first message
              const showDateBreaker = 
                index === 0 || 
                (previousMessageDate && 
                  currentMessageDate.toDateString() !== previousMessageDate.toDateString());

              return (
                <Box key={message.id}>
                  {showDateBreaker && (
                    <Typography 
                      variant="caption" 
                      color="text.secondary" 
                      sx={{ 
                        display: 'block', 
                        textAlign: 'center',
                        my: 2,
                        fontWeight: 500,
                      }}
                    >
                      {format(currentMessageDate, 'EEEE, MMMM d, yyyy')}
                    </Typography>
                  )}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: isUser ? 'flex-end' : 'flex-start',
                      mb: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: isUser ? 'row-reverse' : 'row',
                        alignItems: 'flex-end',
                        gap: 1,
                        maxWidth: '70%',
                      }}
                    >
                      {/* Avatar */}
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32, 
                          bgcolor: isAdmin ? brandColors.gold : brandColors.indigo,
                          flexShrink: 0,
                        }}
                      >
                        {isAdmin ? <SupportIcon sx={{ fontSize: 18 }} /> : <PersonIcon sx={{ fontSize: 18 }} />}
                      </Avatar>
                      
                      {/* Message Bubble */}
                      <Box
                        sx={{
                          position: 'relative',
                          px: 2,
                          py: 1.5,
                          borderRadius: 2.5,
                          backgroundColor: isUser ? brandColors.indigo : '#f0f0f0',
                          color: isUser ? 'white' : 'text.primary',
                          // SMS-style bubble tail
                          '&::before': isUser ? {
                            content: '""',
                            position: 'absolute',
                            right: -7,
                            bottom: 10,
                            width: 0,
                            height: 0,
                            borderLeft: '8px solid ' + brandColors.indigo,
                            borderTop: '4px solid transparent',
                            borderBottom: '4px solid transparent',
                          } : {
                            content: '""',
                            position: 'absolute',
                            left: -7,
                            bottom: 10,
                            width: 0,
                            height: 0,
                            borderRight: '8px solid #f0f0f0',
                            borderTop: '4px solid transparent',
                            borderBottom: '4px solid transparent',
                          },
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {message.content}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            display: 'block',
                            mt: 0.5,
                            opacity: isUser ? 0.9 : 0.6,
                            fontSize: '0.7rem',
                          }}
                        >
                          {format(new Date(message.createdAt), 'h:mm a')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </Stack>
        )}
      </Paper>

      {/* Reply Section */}
      {!isThreadClosed ? (
        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Type your reply here..."
              value={replyContent}
              onChange={(e) => setReplyContent(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSubmitting}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper',
                },
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Press Ctrl+Enter to send
              </Typography>
              <Button
                variant="contained"
                endIcon={<SendIcon />}
                onClick={handleSendReply}
                disabled={!replyContent.trim() || isSubmitting}
                sx={{
                  backgroundColor: brandColors.gold,
                  '&:hover': {
                    backgroundColor: '#C08500', // darkGold
                  },
                }}
              >
                {isSubmitting ? 'Sending...' : 'Send Reply'}
              </Button>
            </Box>
          </Stack>
        </Paper>
      ) : (
        <Alert severity="info">
          This conversation is {thread?.status}. You cannot send new messages.
        </Alert>
      )}
    </Container>
  );
};

export default MessageThreadPage;